// Admin-only quick search — type UDISE or school name, hit a result, jump
// straight to that school's Setup page (acting as that tenant). Sits inline in
// the navbar next to the logout button. Hidden for non-admin users.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2 } from "lucide-react";
import { useActiveSchool } from "@/lib/activeSchool";

export default function AdminSchoolSearch() {
  const { profiles, setActiveId } = useActiveSchool();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return profiles.slice(0, 20);
    return profiles
      .filter((p) => {
        const name = (p.schoolName || "").toLowerCase();
        const udise = (p.udiseCode || "").toLowerCase();
        return name.includes(needle) || udise.includes(needle);
      })
      .slice(0, 20);
  }, [profiles, q]);

  const pick = (p) => {
    setActiveId(p.id);
    setOpen(false);
    setQ("");
    // Admin opens that school's profile/Setup page so the masters table is
    // visible immediately.
    navigate("/setup");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { if (filtered[activeIdx]) pick(filtered[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="admin-school-search">
      <div className="flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1.5 focus-within:border-clay">
        <Search className="h-3.5 w-3.5 text-ink-700" />
        <input
          type="text"
          value={q}
          placeholder="Search school by UDISE / name…"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActiveIdx(0); }}
          onKeyDown={onKey}
          data-testid="admin-school-search-input"
          className="w-44 bg-transparent text-xs text-ink-900 placeholder:text-ink-700 focus:outline-none sm:w-56"
        />
      </div>
      {open && (
        <div
          className="absolute right-0 z-40 mt-1 max-h-80 w-72 overflow-y-auto rounded-md border border-border bg-white shadow-lg"
          data-testid="admin-school-search-dropdown"
        >
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-ink-700">No matching schools.</p>
          ) : (
            <ul>
              {filtered.map((p, i) => (
                <li key={p.id}>
                  <button
                    onClick={() => pick(p)}
                    onMouseEnter={() => setActiveIdx(i)}
                    data-testid={`admin-school-search-result-${p.udiseCode || p.id}`}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors ${i === activeIdx ? "bg-clay/10 text-clay-dark" : "text-ink-900 hover:bg-sand-50"}`}
                  >
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-clay" />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-semibold">{p.schoolName || "(unnamed school)"}</span>
                      <span className="block truncate text-[0.65rem] text-ink-700">UDISE {p.udiseCode || "—"} · {p.district || ""}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
